import React from "react";

export default function UseCases() {
  return (
    <div style={styles.container}>

      {/* HEADER */}
      <div style={{ textAlign: "center", marginBottom: "40px" }}>
        <h1 style={styles.title}>FentaSafe Use Cases</h1>
      </div>

      <p style={styles.intro}>
        FentaSafe is an AI-driven early warning platform designed to detect
        environmental and safety risks before incidents escalate. The platform
        continuously monitors signals such as air quality (PM2.5), volatile
        compounds (VOC), chemical exposure indicators, and contextual risk
        patterns to provide real-time alerts, predictive insights, and operator
        decision support across K-12 schools and public safety environments.
      </p>

      {/* USE CASE GRID */}
      <div style={styles.grid}>

        {/* SCHOOL SAFETY — Primary market, richest card */}
        <div style={{ ...styles.card, ...styles.primaryCard }}>
          <h2>🏫 K-12 School Safety</h2>
          <div style={styles.primaryBadge}>Primary Use Case</div>
          <p>
            Proactive environmental and contextual risk intelligence designed
            specifically for K-12 schools — detecting threats before they
            escalate into critical incidents.
          </p>
          <ul>
            <li>Environmental signal monitoring (PM2.5, VOC, chemical exposure)</li>
            <li>Early risk indicators for campus safety teams</li>
            <li>Centralized multi-zone dashboards for administrators</li>
            <li>Human-supervised alerting and intervention workflows</li>
            <li>Aligned with SchoolSafety.gov threat assessment frameworks</li>
            <li>
              <strong style={{ color: "#fbbf24" }}>
                SVPP-eligible: school districts can apply for up to $500,000
                in COPS Office grant funding for FentaSafe deployment
              </strong>
            </li>
          </ul>
        </div>

        <div style={styles.card}>
          <h2>🚨 Public Safety Monitoring</h2>
          <p>
            Multi-signal monitoring across public environments to detect
            evolving risks early.
          </p>
          <ul>
            <li>Real-time environmental monitoring</li>
            <li>Multi-signal risk detection and correlation</li>
            <li>Early alert generation before escalation</li>
            <li>Situational awareness dashboards</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h2>🧠 Community Risk Awareness</h2>
          <p>
            Supports communities facing opioid exposure challenges with early
            environmental awareness.
          </p>
          <ul>
            <li>Early environmental risk indicators</li>
            <li>Community-level dashboards</li>
            <li>Public health collaboration support</li>
            <li>Preventive monitoring</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h2>🚑 Emergency Response</h2>
          <p>
            Improves response outcomes with early awareness and contextual
            alerts before incidents escalate.
          </p>
          <ul>
            <li>Incident awareness dashboards</li>
            <li>Early escalation indicators</li>
            <li>Operator decision support</li>
            <li>Real-time situational awareness</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h2>🌆 Smart City Safety</h2>
          <p>
            Integrated safety intelligence across urban infrastructure and
            facilities.
          </p>
          <ul>
            <li>Multi-zone monitoring</li>
            <li>Urban safety dashboards</li>
            <li>Cross-facility risk visibility</li>
            <li>Integrated safety insights</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h2>🔬 Research & NGO Programs</h2>
          <p>
            Supports controlled pilots, evaluation studies, community outreach
            initiatives, and safety research.
          </p>
          <ul>
            <li>Pilot program monitoring</li>
            <li>Research data collection and validation</li>
            <li>Impact evaluation support</li>
            <li>Community engagement initiatives</li>
            <li>Research collaboration with ICMR-NINE (in progress)</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h2>🎓 Higher Education & Campus Safety</h2>
          <p>
            Proactive environmental and safety intelligence for college and
            university campuses, where dense populations, laboratories, and
            residential facilities create early-warning needs similar to K-12
            environments — at a larger, more distributed scale.
          </p>
          <ul>
            <li>Dormitory and residence-hall environmental monitoring</li>
            <li>Laboratory and research-building oversight</li>
            <li>Multi-building, multi-zone dashboards for facilities and campus safety staff</li>
            <li>Human-supervised alerting coordinated with campus security and health services</li>
          </ul>
        </div>

      </div>

      {/* EXECUTIVE SUMMARY */}
      <div style={styles.summaryCard}>
        <h2>Executive Use Case Summary</h2>
        <p>
          FentaSafe supports schools, public safety agencies, community
          organizations, researchers, and facility operators by transforming
          environmental and contextual signals into actionable safety intelligence.
        </p>
        <p style={{ marginTop: 12 }}>
          <strong>Primary market:</strong> K-12 school districts, which can fund
          FentaSafe deployment through the COPS Office School Violence Prevention
          Program (SVPP — up to $500,000 per district). NSF SBIR Phase I funding
          supports the continued R&D validation of the platform's AI risk
          intelligence architecture, creating a clear pathway from research to
          commercial deployment.
        </p>
      </div>

      {/* PILOT SECTION */}
      <div style={styles.pilotSection}>
        <h2>🚀 Pilot Deployment Readiness</h2>

        <p>
          FentaSafe is available for controlled pilot deployments in real
          environments.
        </p>

        <ul>
          <li>Limited-area deployment (3–5 zones)</li>
          <li>Minimal setup (sensor + cloud dashboard)</li>
          <li>Privacy-first design (no cameras or tracking)</li>
          <li>Role-based access for administrators, security, and health staff</li>
        </ul>

        <p style={styles.bold}>
          👉 Suitable for schools, campuses, public facilities, and safety organizations
        </p>

        <p style={styles.bold}>
          👉 Cloud-native deployment with scalable monitoring infrastructure
        </p>

        <p style={styles.bold}>
          👉 School districts: eligible for SVPP grant funding (up to $500,000 per district)
        </p>

        <div style={styles.demoRow}>
          <span style={styles.demoText}>
            Explore the interactive demonstration environment.
          </span>
          <a href="/demo" style={styles.demoButton}>
            View Interactive Demo →
          </a>
        </div>
      </div>

    </div>
  );
}

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "40px 20px",
    color: "#e5e7eb",
  },

  title: {
    fontSize: "2.2rem",
    marginBottom: "20px",
  },

  intro: {
    marginBottom: "30px",
    lineHeight: "1.7",
    color: "#cbd5f5",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
  },

  card: {
    background: "#020617",
    padding: "20px",
    borderRadius: "10px",
    border: "1px solid #1e293b",
    lineHeight: "1.6",
  },

  primaryCard: {
    border: "1px solid rgba(251, 191, 36, 0.35)",
    background: "rgba(251, 191, 36, 0.04)",
    gridColumn: "1 / -1", // full width on first row
  },

  primaryBadge: {
    display: "inline-block",
    background: "rgba(251, 191, 36, 0.15)",
    border: "1px solid rgba(251, 191, 36, 0.4)",
    color: "#fbbf24",
    borderRadius: 6,
    padding: "2px 12px",
    fontSize: "0.78rem",
    fontWeight: 700,
    marginBottom: 12,
    marginTop: -4,
  },

  summaryCard: {
    marginTop: "40px",
    marginBottom: "30px",
    padding: "30px",
    background: "#020617",
    borderRadius: "10px",
    border: "1px solid #1e293b",
    textAlign: "center",
    lineHeight: 1.7,
    color: "#cbd5e1",
  },

  pilotSection: {
    marginTop: "10px",
    padding: "30px",
    background: "#020617",
    borderRadius: "10px",
    border: "1px solid #1e293b",
    lineHeight: 1.7,
  },

  demoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "25px",
    flexWrap: "wrap",
    gap: "15px",
  },

  demoText: {
    color: "#94a3b8",
    fontSize: "0.95rem",
  },

  demoButton: {
    display: "inline-block",
    padding: "14px 28px",
    background: "#f59e0b",
    color: "#ffffff",
    textDecoration: "none",
    borderRadius: "8px",
    fontSize: "1rem",
    fontWeight: "700",
    boxShadow: "0 4px 12px rgba(245,158,11,0.3)",
  },

  bold: {
    marginTop: "15px",
    fontWeight: "600",
  },
};
